import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

import SparkChart from "./SparkChart";
import DeviceRecentCallList from "./DeviceRecentCallList";
import { sortByDateInReverse } from "../../../Utils";

import { useTheme, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Box } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import moment from "moment";

const useStyles = (theme) => ({
    tableRow: {
        width: "95%",
        margin: "auto",
    },
    card: {
        borderRadius: 0,
    },
    deviceId: {
        fontSize: ".9rem",
        fontWeight: "700",
        marginBottom: ".5rem",
        textDecoration: "none",
        color: "#1976d2",
        "&:visited": {
            color: "#1976d2",
        },
        [theme.breakpoints.down('xl')]: {
            fontSize: ".8rem",
        },
    },
    lidarButton: {
        position: "relative",
        color: "#1976d2",
        "&:hover": {
            cursor: "pointer",
            color: "#1976d2",
        },
        "&:visited": {
            color: "#1976d2",
        },
    },
    rowHeader: {
        display: "flex",
        alignItems: "center",
        width: "25%",
        fontWeight: "700",
        [theme.breakpoints.down('xl')]: {
            width: "35%",
        },
    },
    rowDeviceId: {
        paddingLeft: "2rem",
        [theme.breakpoints.down('xl')]: {
            paddingLeft: "1rem",
        },
    },
    rowSiteName: {
        width: "100%",
        paddingLeft: "2rem",
        whiteSpace: "pre-line",
        [theme.breakpoints.down('xl')]: {
            paddingLeft: "1rem",
            fontSize: ".8rem",
        },
    },
    rowChart: {
        display: "flex",
        justifyContent: "center",
        width: "40%",
        height: "75px",
        overflow: "hidden",
    },
    rowDttm: {
        width: "15%",
    },
    rowButton: {
        width: "5%",
        display: "flex",
        justifyContent: "flex-end",
    },
    expandIconContainer: {
        paddingRight: "2rem",
    },
    toggleRecentCallsIcon: {
        color: "#1976d2",
        "&:hover": {
            cursor: "pointer",
        },
    },
});

export default function DashboardListItem({
    d,
    fields,
    value,
    lineType,
    deviceId,
    siteName,
    indicatorColor,
    deviceType,
}) {
    const theme = useTheme();
    const classes = useStyles(theme);
    const [lastCall, setLastCall] = useState("");
    const [showRecentCalls, setShowRecentCalls] = useState(false);
    const [messages, setMessages] = useState(null);
    const [minutesLastCall, setMinutesLastCall] = useState("");
    const timestampFields = ["dttm", "received_timestamp", "dttm"];
    const timestamp = timestampFields[value];

    const setLastCallDifference = useCallback(
        (sortedMessages, now) => {
            if (sortedMessages) {
                let rtuMessages = sortedMessages;
                let recentCallDttm = rtuMessages[0][timestamp];
                let callDttm = moment(recentCallDttm);
                let currentDttm = moment(now);
                let diff = currentDttm.diff(callDttm, "minutes");

                setMinutesLastCall(diff);
            }
        },
        [timestamp]
    );

    useEffect(() => {
        if (d?.length) {
            let sortedMessages = sortByDateInReverse(d, timestamp);
            let recentMessages = sortedMessages.slice(0, 10);

            let lastCallDttm = recentMessages?.[recentMessages.length - 1][timestamp]
                ? moment(recentMessages[recentMessages.length - 1][timestamp])
                      .utc()
                      .format("MM/DD HH:mm")
                : "";

            setLastCall(lastCallDttm);
            setMessages(recentMessages);
        }
    }, [d, deviceId, timestamp]);

    const formatSiteName = () => {
        if (Array.isArray(siteName)) {
            return siteName.map((name, index) => (
                <span key={index}>
                    {name}
                    {<br />}
                </span>
            ));
        } else {
            return siteName;
        }
    };

    useEffect(() => {
        setLastCallDifference(messages, new Date());

        const loop = () => {
            setInterval(() => {
                setLastCallDifference(messages, new Date());
            }, 6000);
        };

        return () => {
            clearInterval(loop);
        };
    }, [messages, setLastCallDifference]);

    const toggleRecentCallsList = () => {
        setShowRecentCalls(!showRecentCalls);
    };

    return (
        <TableContainer key={deviceId + siteName} component={Paper}>
            <Table sx={classes.table} aria-label="simple table">
                <TableBody>
                    <TableRow>
                        <TableCell id="tableCell" component="th" scope="row" align="center">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "95%",
                                    margin: "auto",
                                }}
                            >
                                <Box component="div" sx={classes.expandIconContainer}>
                                    {showRecentCalls ? (
                                        <Tooltip title="Hide Recent Calls">
                                            <ExpandMoreIcon
                                                onClick={toggleRecentCallsList}
                                                sx={classes.toggleRecentCallsIcon}
                                                fontSize={"large"}
                                                style={{ height: "20px", width: "20px" }}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Show Recent Calls">
                                            <ArrowForwardIosIcon
                                                onClick={toggleRecentCallsList}
                                                sx={classes.toggleRecentCallsIcon}
                                                fontSize={"small"}
                                                style={{ height: "20px", width: "20px" }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box component="div" sx={classes.rowHeader}>
                                    <Box component="div" sx={indicatorColor}></Box>
                                    <Box component="div" sx={classes.rowDeviceId}>
                                        {value === 1 ? (
                                            <div>
                                                <h3>({deviceId})</h3>
                                            </div>
                                        ) : (
                                            <Link
                                                sx={classes.deviceId}
                                                to={{ pathname: `/telemetry/device-health/${deviceType}/${deviceId}` }}
                                            >
                                                <h3>({deviceId})</h3>
                                            </Link>
                                        )}
                                    </Box>

                                    <Box component="div" sx={classes.rowSiteName}>{formatSiteName()}</Box>
                                </Box>
                                <Box component="div" sx={classes.rowDttm}>
                                    {lastCall}
                                    <br />({minutesLastCall} minutes ago)
                                </Box>
                                <Box component="div" sx={classes.rowChart}>
                                    <SparkChart
                                        device={d}
                                        field={fields[value]}
                                        lineType={lineType}
                                        chartType={"dashboard"}
                                        height={"50px"}
                                        width={"75%"}
                                    />
                                </Box>
                                {value === 0 && (
                                    <Box component="div" sx={classes.rowButton}>
                                        <Tooltip title="View Point Cloud">
                                            <Link
                                                sx={classes.lidarButton}
                                                to={{ pathname: `/telemetry/device-health/lidar-view/${deviceId}` }}
                                            >
                                                <TrackChangesIcon />
                                            </Link>
                                        </Tooltip>
                                    </Box>
                                )}
                            </div>
                            {messages ? (
                                <div style={{ width: "95%", margin: "auto" }}>
                                    <DeviceRecentCallList
                                        deviceId={deviceId}
                                        d={messages}
                                        itemType={"list"}
                                        showCalls={showRecentCalls}
                                        deviceType={deviceType}
                                    />
                                </div>
                            ) : null}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}
